import { Controller } from "@hotwired/stimulus";

// Publish form state stays in the form. Select2 only supplies searchable pickers;
// Rails and Reveal independently normalize the submitted sharing state.
export default class extends Controller {
  static values = {
    orgSearchUrl: String,
    providerSearchUrl: String,
    userSearchUrl: String,
    probeUrl: String,
    coverageUrl: String,
    exclusionsSupported: Boolean
  };

  connect() {
    this.dirty = false;
    this.selects = [...this.element.querySelectorAll("select")];
    this.initPicker("org-select", this.orgSearchUrlValue, "Search for an organization...", "1");
    this.initPicker("sub-org-select", this.orgSearchUrlValue, "Search for a sub-organization...", "0");
    this.initPicker("excluded-org-select", this.orgSearchUrlValue, "Search for a canonical organization to exclude...", "1");
    this.initPicker("provider-select", this.providerSearchUrlValue, "Search for a provider...");
    this.initPicker("excluded-provider-select", this.providerSearchUrlValue, "Search for a provider to exclude...");
    this.initPicker("user-select", this.userSearchUrlValue, "Search for a user...");
    this.initPicker("probe-user-select", this.userSearchUrlValue, "Pick a user to test access...");
    this.sync();
    window.addEventListener("beforeunload", this.beforeUnload);
  }

  disconnect() {
    this.invalidateCoverage();
    window.removeEventListener("beforeunload", this.beforeUnload);
    if (!window.$?.fn.select2) return;
    this.selects.forEach(el => {
      window.$(el).off(".revealPublish");
      if (el.classList.contains("select2-hidden-accessible")) window.$(el).select2("destroy");
    });
  }

  field(id) { return this.element.querySelector(`#${id}`); }
  get isDraft() { return this.element.querySelector('input[name="visibility"]:checked').value === "draft"; }
  get allOrgs() { return this.field("all_organizations").checked; }
  get allProviders() { return this.field("all_providers").checked; }

  initPicker(id, url, placeholder, canonical) {
    if (!window.$?.fn.select2) return;
    window.$(this.field(id)).select2({
      theme: "bootstrap-5", placeholder, allowClear: true, minimumInputLength: 1,
      ajax: {
        url, dataType: "json", delay: 250,
        data: params => ({
          term: params.term,
          ...(canonical === undefined ? {} : { canonical }),
          ...(["provider-select", "excluded-provider-select"].includes(id) ?
            { exclude: [...this.field(id).selectedOptions].map(option => option.value) } : {})
        }),
        processResults: data => ({
          results: id === "user-select" ? data.results.map(result => ({
            ...result,
            id: result.canonical_organization_id ? `${result.id}:${result.canonical_organization_id}` : result.id
          })) : data.results
        })
      }
    }).on("change.revealPublish", () => { if (id !== "probe-user-select") this.markDirty(); });
  }

  clear(...ids) {
    ids.forEach(id => {
      const el = this.field(id);
      [...el.options].forEach(option => { option.selected = false; });
      window.$?.(el).trigger("change.select2");
    });
  }

  changed(event) {
    if (event.target.id === "probe-user-select") return;
    if (event.target.name === "visibility" && this.isDraft) {
      this.field("all_organizations").checked = false;
      this.field("all_providers").checked = false;
      this.clear("org-select", "sub-org-select", "provider-select", "user-select", "excluded-org-select", "excluded-provider-select");
    }
    if (event.target.id === "all_organizations") {
      if (this.allOrgs) {
        // The backend supersedes only specific organization grants. Provider
        // and contextual user grants remain additive restoration paths.
        this.clear("org-select", "sub-org-select");
      } else {
        this.clear("excluded-org-select");
      }
    }
    if (event.target.id === "all_providers") {
      this.clear(this.allProviders ? "provider-select" : "excluded-provider-select");
    }
    this.sync();
    this.markDirty();
  }

  sync() {
    this.field("publish-channels").disabled = this.isDraft;
    this.field("all_providers").disabled = false;
    const disabled = {
      "org-select": this.allOrgs,
      "sub-org-select": this.allOrgs,
      "user-select": false,
      "provider-select": this.allProviders,
      "excluded-org-select": !this.exclusionsSupportedValue || !this.allOrgs,
      "excluded-provider-select": !this.exclusionsSupportedValue || !this.allProviders
    };
    Object.entries(disabled).forEach(([id, value]) => {
      this.field(id).disabled = value || this.isDraft;
      window.$?.(this.field(id)).trigger("change.select2");
    });
  }

  markDirty() {
    this.invalidateCoverage();
    this.dirty = true;
    this.field("dirty-banner").style.display = "";
    const button = this.field("publish-submit");
    button.classList.remove("btn-outline-primary");
    button.classList.add("btn-primary");
  }

  beforeUnload = event => {
    if (!this.dirty) return;
    event.preventDefault();
    event.returnValue = "";
  };

  submit(event) {
    const hasGrants = ["org-select", "sub-org-select", "provider-select", "user-select"]
      .some(id => this.field(id).selectedOptions.length > 0);
    if (!this.isDraft && !this.allOrgs && !this.allProviders && !hasGrants &&
        // eslint-disable-next-line no-alert -- Preserve the existing empty-audience confirmation.
        !window.confirm("You selected Published but haven't chosen any organizations, providers or users. The dashboard will be visible only to you — same as Draft. Save anyway?")) {
      event.preventDefault();
      return;
    }
    this.dirty = false;
  }

  requestHeaders(accept) {
    return { Accept: accept, "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" };
  }

  async probe() {
    const userId = this.field("probe-user-select").value;
    if (!userId) return this.renderProbe(null, "Pick a user first.");
    const body = new FormData(this.element);
    // The publish form PATCH override must not change the probe's POST route.
    body.delete("_method");
    body.set("user_id", userId);
    this.field("probe-btn").disabled = true;
    try {
      const response = await fetch(this.probeUrlValue, {
        method: "POST", body, credentials: "same-origin",
        headers: this.requestHeaders("application/json")
      });
      if (!response.ok) throw new Error("Probe request failed");
      this.renderProbe(await response.json());
    } catch {
      this.renderProbe(null, "Probe request failed. Try again.");
    } finally {
      this.field("probe-btn").disabled = false;
    }
  }

  invalidateCoverage() {
    this.coverageRequest?.abort();
    this.coverageRequest = null;
    const target = this.field("rls-coverage-result");
    if (!target) return;
    target.replaceChildren();
    target.style.display = "none";
    target.removeAttribute("aria-busy");
    this.field("rls-coverage-btn").disabled = false;
  }

  async coverage() {
    if (this.coverageRequest) return;
    const request = new AbortController();
    this.coverageRequest = request;
    const target = this.field("rls-coverage-result");
    const button = this.field("rls-coverage-btn");
    const body = new FormData(this.element);
    body.delete("_method");
    body.delete("probe_user_id");
    target.style.display = "";
    target.textContent = "Checking…";
    target.setAttribute("aria-busy", "true");
    button.disabled = true;
    try {
      const response = await fetch(this.coverageUrlValue, {
        method: "POST", body, credentials: "same-origin", signal: request.signal,
        headers: this.requestHeaders("text/html")
      });
      if (!response.ok || response.redirected) throw new Error("Coverage check failed");
      const html = response.status === 204 ? null : await response.text();
      if (request.signal.aborted) return;
      if (html) target.innerHTML = html;
      else target.textContent = "Couldn’t reach rx-reveal, so nothing was checked.";
    } catch {
      if (!request.signal.aborted) target.textContent = "Coverage check failed. Try again.";
    } finally {
      if (this.coverageRequest === request && !request.signal.aborted) {
        this.coverageRequest = null;
        button.disabled = false;
        target.removeAttribute("aria-busy");
        target.focus();
      }
    }
  }

  renderProbe(data, error) {
    const el = this.field("probe-result");
    el.style.display = "";
    el.replaceChildren();
    el.className = `alert mt-2 ${error ? "alert-warning" : data.access ? "alert-success" : "alert-danger"}`;
    if (error) { el.textContent = error; return; }
    const label = document.createElement("strong");
    label.textContent = data.access ? "Access granted: " : "No access: ";
    el.append(label, document.createTextNode(data.reason));
  }
}
